import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../utils/UserContext';
import { getRoleAccent, getRoleLabel } from '../data/map';

// ─── Rotating conic gradient ring ───────────────────────────
const RotatingRing = ({ accent, size, ringWidth, children }) => {
  const canvasRef = useRef(null);
  const angleRef  = useRef(0);
  const rafRef    = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const s   = size * dpr;
    canvas.width  = s;
    canvas.height = s;
    canvas.style.width  = `${size}px`;
    canvas.style.height = `${size}px`;

    // Parse accent hex → rgb
    const hex = accent.replace('#', '');
    const r = parseInt(hex.slice(0,2), 16);
    const g = parseInt(hex.slice(2,4), 16);
    const b = parseInt(hex.slice(4,6), 16);

    const cx = s / 2;
    const cy = s / 2;
    const outerR = cx;

    const draw = () => {
      ctx.clearRect(0, 0, s, s);
      angleRef.current += 0.012;
      const a = angleRef.current;

      // Draw arc segments — bright lead + fading tail
      const segments = 120;
      for (let i = 0; i < segments; i++) {
        const t     = i / segments;
        const theta = a + t * Math.PI * 2;
        const alpha = Math.pow(t, 1.5) * 0.9;

        ctx.beginPath();
        ctx.arc(cx, cy, outerR - (ringWidth * dpr) / 2, theta, theta + 0.07);
        ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.lineWidth   = ringWidth * dpr;
        ctx.stroke();
      }

      // Bright lead dot
      const lx = cx + (outerR - (ringWidth * dpr) / 2) * Math.cos(a + Math.PI * 2);
      const ly = cy + (outerR - (ringWidth * dpr) / 2) * Math.sin(a + Math.PI * 2);
      const grd = ctx.createRadialGradient(lx, ly, 0, lx, ly, ringWidth * dpr * 1.5);
      grd.addColorStop(0, `rgba(${r},${g},${b},1)`);
      grd.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.beginPath();
      ctx.arc(lx, ly, ringWidth * dpr * 1.5, 0, Math.PI * 2);
      ctx.fillStyle = grd;
      ctx.fill();

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, [accent, size, ringWidth]);

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', top: 0, left: 0, borderRadius: '50%' }}
      />
      <div style={{
        position: 'absolute',
        top: ringWidth + 3,
        left: ringWidth + 3,
        right: ringWidth + 3,
        bottom: ringWidth + 3,
        borderRadius: '50%',
        overflow: 'hidden',
      }}>
        {children}
      </div>
    </div>
  );
};

// ─── Main AvatarRing component ───────────────────────────────
// Props:
//   size        {number}  — outer diameter in px (default 80)
//   ringWidth   {number}  — ring thickness in px (default 3)
//   showName    {boolean} — show name + role label below (default true)
//   showRole    {boolean} — show role badge (default true)
//   compact     {boolean} — smaller layout for InnerTabsd header (default false)
//   homeRoute   {string}  — where the avatar click goes (null = not clickable)
//   accent      {string}  — optional override; otherwise derived from the user's Group/userJob
const AvatarRing = ({
  size      = 80,
  ringWidth = 3,
  showName  = true,
  showRole  = true,
  compact   = false,
  homeRoute = '/profile',
  accent: accentProp,
}) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const userName  = user?.userName || user?.username || 'User';
  const accent    = accentProp || getRoleAccent(user);   // IGLU orange · Client blue · staff role colour
  const roleLabel = getRoleLabel(user);
  const [imgFailed, setImgFailed] = useState(false);

  // Headshot URL — for clients this may be their company logo
  const headshotUrl = user?.headshot || user?.avatar || user?.profileImage || null;
  useEffect(() => { setImgFailed(false); }, [headshotUrl]);
  const initials = userName
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div style={{
      display: 'flex',
      flexDirection: compact ? 'row' : 'column',
      alignItems: 'center',
      gap: compact ? 10 : 8,
    }}>
      <RotatingRing accent={accent} size={size} ringWidth={ringWidth}>
        <div
          onClick={() => homeRoute && navigate(homeRoute)}
          title="Go to profile"
          style={{ width: '100%', height: '100%', cursor: homeRoute ? 'pointer' : 'default' }}
          onMouseEnter={e => { if (homeRoute) e.currentTarget.style.opacity = '0.75'; }}
          onMouseLeave={e => { if (homeRoute) e.currentTarget.style.opacity = '1'; }}
        >
          {headshotUrl && !imgFailed ? (
            <img
              src={headshotUrl}
              alt={userName}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={() => setImgFailed(true)}
            />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              background: `linear-gradient(135deg, ${accent}30 0%, ${accent}15 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: compact ? size * 0.28 : size * 0.32,
              fontWeight: 600,
              color: accent,
              letterSpacing: '0.05em',
              fontFamily: 'system-ui, sans-serif',
            }}>
              {initials}
            </div>
          )}
        </div>
      </RotatingRing>

      {(showName || showRole) && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: compact ? 'flex-start' : 'center',
          gap: 2,
        }}>
          {showName && (
            <span style={{
              fontSize: compact ? 13 : 14,
              fontWeight: 600,
              color: '#e2e8f0',
              letterSpacing: '0.01em',
              fontFamily: 'system-ui, sans-serif',
              lineHeight: 1.2,
            }}>
              {userName}
            </span>
          )}
          {showRole && (
            <span style={{
              fontSize: compact ? 10 : 11,
              fontWeight: 500,
              color: accent,
              background: `${accent}18`,
              border: `1px solid ${accent}35`,
              borderRadius: 20,
              padding: compact ? '1px 7px' : '2px 10px',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontFamily: 'system-ui, sans-serif',
              lineHeight: 1.6,
            }}>
              {roleLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default AvatarRing;